<main class="onboarding-documents-page p-4 p-xxl-5">
  <section class="card border rounded-3 overflow-hidden onboarding-documents-card">
    <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
      <div class="d-inline-flex align-items-center flex-wrap border rounded-2 bg-light p-1" role="group" aria-label="Document filters">
        <button
          v-for="filter in filters"
          :key="filter.label"
          type="button"
          @click="set_active_filter(filter.tone)"
          :class="[
            'btn btn-sm d-inline-flex align-items-center gap-2 px-3 py-1',
            active_filter === filter.tone ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
        >
          <span>{{ filter.label }}</span>
          <span v-if="filter.count">{{ filter.count }}</span>
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
        <label class="onboarding-queue-search d-inline-flex align-items-center">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input v-model="table_search" type="search" placeholder="Search table" @input="reset_table_page">
        </label>
        <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
          <i class="bi bi-funnel"></i>
          <span>Filters</span>
        </button>
        <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
          <i class="bi bi-download"></i>
          <span>Export</span>
        </button>
      </div>
    </div>

    <div class="table-responsive onboarding-documents-table-wrap">
      <table class="table align-middle mb-0 onboarding-documents-table">
        <thead class="table-light">
          <tr>
            <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">Case</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Journey</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Progress</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Risk</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Stage</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Owner</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Activation</th>
            <th class="text-uppercase text-muted small fw-semibold py-3 pe-4">Action</th>
          </tr>
        </thead>
        <tbody v-if="is_loading_documents">
          <tr>
            <td colspan="8" class="text-center text-muted py-5">Loading documents...</td>
          </tr>
        </tbody>
        <tbody v-else-if="documents_error">
          <tr>
            <td colspan="8" class="py-4">
              <div class="alert alert-warning mb-0">{{ documents_error }}</div>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="filtered_documents.length === 0">
          <tr>
            <td colspan="8" class="text-center text-muted py-5">No onboarding applications with documents found.</td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="item in paginated_documents"
            :key="item.id"
            class="cursor-pointer"
            tabindex="0"
            @click="open_document_detail(item)"
            @keyup.enter="open_document_detail(item)"
          >
            <td class="ps-4 py-3">
              <div class="fw-bold text-black">{{ item.case_name }}</div>
              <div class="small text-muted mt-1">
                {{ item.status }} · {{ item.document_count }} document<span v-if="item.document_count !== 1">s</span>
              </div>
            </td>
            <td class="py-3">
              <span class="d-inline-flex align-items-center gap-2">
                <span class="rounded-circle d-inline-block onboarding-journey-dot" :style="journey_dot_style(item.journey_tone)"></span>
                <span>{{ item.type }}</span>
              </span>
            </td>
            <td class="py-3">
              <div class="d-flex align-items-center gap-3">
                <div class="progress flex-shrink-0 onboarding-progress-track-compact">
                  <div
                    class="progress-bar onboarding-progress-bar"
                    :style="{ '--onboarding-progress-size': `${item.progress}%`, '--onboarding-progress-color': progress_bar_color(item.progress_tone) }"
                  ></div>
                </div>
                <span class="small text-muted">{{ item.progress }}%</span>
              </div>
            </td>
            <td class="py-3">
              <span :class="['badge rounded-pill fw-normal', risk_badge_class(item.risk_tone)]">
                {{ item.risk }}
              </span>
            </td>
            <td class="py-3">{{ item.stage }}</td>
            <td class="py-3">
              <span class="d-inline-flex align-items-center gap-2">
                <span class="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold text-white onboarding-list-initials-icon">
                  {{ item.owner_initials }}
                </span>
                <span>{{ item.reviewer }}</span>
              </span>
            </td>
            <td class="py-3">
              <span :class="['badge rounded-pill fw-normal', activation_badge_class_for_case(item.activation_tone)]">
                {{ item.activation }}
              </span>
            </td>
            <td class="pe-4 py-3">
              <button
                type="button"
                class="onboarding-queue-view-button"
                @click.stop="open_document_case_page(item, $event)"
              >
                <span>View</span>
                <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePagination
      :page="table_page"
      :page-size="table_page_size"
      :page-size-options="table_page_size_options"
      :total-rows="table_total_rows"
      :page-start="table_page_start"
      :page-end="table_page_end"
      :total-pages="table_total_pages"
      @update:page="set_table_page"
      @update:page-size="set_table_page_size"
    />
  </section>

  <div
    v-if="selected_document"
    class="onboarding-case-detail-backdrop"
    @click="close_document_detail"
  ></div>

  <aside
    v-if="selected_document"
    :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_detail_fullscreen }]"
  >
    <header class="border-bottom p-4">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="text-uppercase text-muted fw-semibold small mb-3">
            {{ detail_header_eyebrow() }}
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
            <h2 class="h5 fw-bold text-black mb-0">{{ selected_document.case_name }}</h2>
            <EntityNameEditButton
              :entity-record="selected_document"
              :current-name="selected_document.case_name"
              @updated="selected_document.case_name = $event.name; if (selected_document.case_item) selected_document.case_item.name = $event.name"
            />
          </div>
          <p class="mb-0 text-muted">{{ detail_header_subtitle() }}</p>
        </div>

        <div class="d-flex align-items-center gap-3 flex-shrink-0">
          <span :class="['badge rounded-pill fw-normal', activation_badge_class()]">
            <span class="me-1">&bull;</span>{{ detail_activation_label() }}
          </span>
          <button
            type="button"
            class="btn btn-link text-muted p-0"
            :aria-label="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            :title="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            @click="toggle_detail_fullscreen"
          >
            <i :class="['bi', is_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
          </button>
          <button
            type="button"
            class="btn btn-link text-muted p-0"
            aria-label="Close document detail"
            @click="close_document_detail"
          >
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </header>

    <div class="onboarding-case-detail-body p-4">
      <nav
        ref="detailTabsScroller"
        class="bg-light rounded-3 p-1 d-flex align-items-center gap-2 mb-4 overflow-auto"
        aria-label="Document detail sections"
      >
        <button
          v-for="tab in detail_tabs"
          :key="tab.id"
          type="button"
          :class="[
            'btn btn-sm flex-fill d-inline-flex align-items-center justify-content-center gap-2',
            active_detail_tab === tab.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
          @click="set_detail_tab(tab.id)"
        >
          <i :class="['bi', tab.icon]"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.count" class="badge rounded-pill text-bg-danger">{{ tab.count }}</span>
        </button>
      </nav>
      <section v-if="active_detail_tab === 'summary'">
        <div v-if="is_loading_detail" class="text-muted mb-4">Loading entity detail...</div>

        <dl class="row g-0 mb-4">
          <template v-for="row in summary_rows" :key="row.label">
            <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
            <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
          </template>
        </dl>

        <section>
          <div class="text-uppercase text-muted fw-semibold small mb-3">Stage timeline</div>
          <div class="onboarding-case-timeline">
            <article
              v-for="item in stage_timeline"
              :key="item.title"
              :class="['onboarding-case-timeline-item', `is-${item.status}`]"
            >
              <span class="onboarding-case-timeline-icon">
                <i :class="['bi', item.icon]"></i>
              </span>
              <div>
                <div class="fw-semibold text-black">{{ item.title }}</div>
                <div class="text-muted small mt-1">{{ item.detail }}</div>
              </div>
            </article>
          </div>
        </section>
      </section>

      <section v-else-if="active_detail_tab === 'agreements'">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="text-uppercase text-muted fw-semibold small">Documents under review</div>
          <div class="text-uppercase text-muted fw-semibold small">{{ selected_case_documents.length }} documents</div>
        </div>

        <div class="d-grid gap-3">
          <p v-if="selected_case_documents.length === 0" class="text-muted mb-0">
            No document records returned for this onboarding.
          </p>

          <article
            v-for="document in selected_case_documents"
            :key="document.id"
            class="border rounded-3 p-3"
          >
            <div class="d-flex align-items-start gap-3">
              <span :class="['rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0 onboarding-icon-square-21', status_badge_class(document.status_tone)]">
                <i :class="['bi', document.status_tone === 'success' ? 'bi-check-circle' : 'bi-file-earmark-text']"></i>
              </span>
              <div class="min-w-0 flex-grow-1">
                <div class="fw-bold text-black mb-1">{{ document.document }}</div>
                <div class="text-muted small">
                  {{ document.type }} &middot; {{ document.file_name || 'Required document' }}
                </div>
                <div v-if="document.verification_comment" class="small text-muted mt-2">
                  Comment: {{ document.verification_comment }}
                </div>
              </div>
              <div class="d-flex flex-column align-items-end gap-3 flex-shrink-0">
                <span :class="['badge rounded-pill', status_badge_class(document.status_tone)]">
                  <span class="me-1">&bull;</span>{{ document.status }}
                </span>
                <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <button
                    v-if="has_document_preview(document)"
                    type="button"
                    class="onboarding-queue-view-button"
                    @click="open_document_preview(document)"
                  >
                    <span>Preview</span>
                  </button>
                  <button
                    v-if="!is_document_review_approved(document)"
                    type="button"
                    class="onboarding-queue-view-button"
                    @click="open_document_form(document, $event)"
                  >
                    <span>View</span>
                    <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
                  </button>
                  <button
                    v-if="is_document_review_supported(document) && !is_document_review_approved(document)"
                    type="button"
                    class="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-2"
                    :disabled="is_document_review_busy(document)"
                    @click="run_document_review(document, false)"
                  >
                    {{ is_document_review_busy(document) ? 'Saving...' : 'Reject' }}
                  </button>
                  <button
                    v-if="is_document_review_supported(document) && !is_document_review_approved(document)"
                    type="button"
                    class="btn btn-sm btn-success d-inline-flex align-items-center gap-2"
                    :disabled="is_document_review_busy(document)"
                    @click="run_document_review(document, true)"
                  >
                    {{ is_document_review_busy(document) ? 'Saving...' : 'Approve' }}
                  </button>
                </div>
              </div>
            </div>
            <div
              v-if="!is_document_review_approved(document) || document.verification_comment"
              class="mt-3 pt-3 border-top"
            >
              <template v-if="is_document_review_approved(document)">
                <div v-if="document.verification_comment" class="small text-muted">
                  Comment: {{ document.verification_comment }}
                </div>
              </template>
              <template v-else>
                <div class="small text-muted mb-2">{{ document_review_status_label(document) }}</div>
                <textarea
                  class="form-control form-control-sm mb-3"
                  rows="2"
                  :disabled="is_document_review_busy(document) || !is_document_review_supported(document)"
                  :value="document_review_comment(document)"
                  @input="set_document_review_comment(document, $event.target.value)"
                  placeholder="Add an optional review comment"
                ></textarea>
                <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                  <div
                    v-if="!is_document_review_supported(document)"
                    class="small text-muted"
                  >
                    {{ unsupported_document_review_reason(document) }}
                  </div>
                  <div
                    v-else-if="document.verification_comment"
                    class="small text-muted"
                  >
                    Latest review note saved.
                  </div>
                </div>
              </template>
            </div>
          </article>

        </div>
      </section>

      <section v-else-if="active_form_section">
        <div class="text-uppercase text-muted fw-semibold small mb-3">{{ active_form_section.label }}</div>
        <section
          v-for="sub_section in active_form_section.sub_sections"
          :key="sub_section.id"
          class="mb-4"
        >
          <div
            v-if="sub_section.label && sub_section.label !== active_form_section.label"
            class="text-uppercase text-muted fw-semibold small mb-3"
          >
            {{ sub_section.label }}
          </div>
          <div
            v-if="active_form_section_is_document_section"
            class="onboarding-document-list"
          >
            <article
              v-for="question in sub_section.questions"
              :key="question.id"
              class="onboarding-document-row"
            >
              <div :class="['onboarding-document-status-icon', document_status_meta(question).icon_class]">
                <i :class="['bi', document_status_meta(question).icon]"></i>
              </div>

              <div class="onboarding-document-row__content">
                <div class="onboarding-document-row__title">
                  {{ question.label }}
                  <span v-if="question.is_mandatory" class="text-danger">*</span>
                </div>
                <div class="onboarding-document-row__detail">
                  {{ document_answer_meta(question).uploaded ? document_answer_meta(question).name : 'Awaiting upload' }}
                </div>
                <div class="onboarding-document-row__meta">
                  <span class="onboarding-document-card__meta">
                    <i :class="['bi', is_document_question(question) ? 'bi-file-earmark-arrow-up' : 'bi-card-text']"></i>
                    <span>{{ is_document_question(question) ? 'Document upload' : 'Submitted response' }}</span>
                  </span>
                </div>
              </div>

              <div class="onboarding-document-row__actions">
                <span :class="['badge rounded-pill onboarding-document-status-pill', document_status_meta(question).pill_class]">
                  {{ document_status_meta(question).label }}
                </span>

                <button
                  v-if="document_answer_meta(question).url"
                  type="button"
                  class="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2 onboarding-document-open-button"
                  @click="open_question_document(question, $event)"
                >
                  <i class="bi bi-box-arrow-up-right"></i>
                  <span>Preview</span>
                </button>
              </div>
            </article>
          </div>

          <div v-else class="onboarding-answer-grid">
            <article
              v-for="question in sub_section.questions"
              :key="question.id"
              class="onboarding-answer-card"
            >
              <p class="onboarding-answer-card__label">
                {{ question.label }}
                <span v-if="question.is_mandatory" class="text-danger">*</span>
              </p>
              <div class="onboarding-answer-card__value">{{ question.answer }}</div>
            </article>
          </div>
        </section>
      </section>
    </div>

    <footer class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3">
      <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
        <i class="bi bi-stars"></i>
        <span>Ask AI</span>
      </button>

      <div class="d-flex align-items-center gap-2">
        <button
          type="button"
          class="btn btn-outline-danger d-inline-flex align-items-center gap-2"
          :disabled="!can_reject_stage || workflow_action_in_progress === 'reject' || workflow_actions_loading"
          @click="run_selected_workflow_action('reject')"
        >
          <i class="bi bi-x-circle"></i>
          <span>{{ workflow_action_in_progress === 'reject' ? 'Rejecting...' : 'Reject' }}</span>
        </button>

        <button
          type="button"
          class="btn btn-outline-warning d-inline-flex align-items-center gap-2"
          :disabled="!can_request_info || workflow_action_in_progress === 'request_info' || workflow_actions_loading"
          @click="run_selected_workflow_action('request_info')"
        >
          <i class="bi bi-send"></i>
          <span>{{ workflow_action_in_progress === 'request_info' ? 'Sending...' : 'Request info' }}</span>
        </button>

        <button
          v-if="can_advance_stage"
          type="button"
          class="btn btn-primary d-inline-flex align-items-center gap-2"
          :disabled="workflow_action_in_progress === 'advance' || workflow_actions_loading"
          @click="run_selected_workflow_action('advance')"
        >
          <i class="bi bi-arrow-right"></i>
          <span>{{ workflow_action_in_progress === 'advance' ? 'Advancing...' : 'Advance stage' }}</span>
        </button>
      </div>
    </footer>

    <div v-if="workflow_actions_loading || workflow_actions_error || workflow_action_feedback || document_review_feedback" class="px-3 pb-3">
      <div v-if="workflow_actions_loading" class="small text-muted">Loading workflow actions...</div>
      <div v-else-if="workflow_actions_error" class="alert alert-warning mb-0 py-2 px-3 small">{{ workflow_actions_error }}</div>
      <div
        v-else-if="workflow_action_feedback"
        :class="['alert mb-0 py-2 px-3 small', workflow_action_feedback_tone === 'success' ? 'alert-success' : workflow_action_feedback_tone === 'danger' ? 'alert-danger' : 'alert-warning']"
      >
        {{ workflow_action_feedback }}
      </div>
      <div
        v-if="document_review_feedback"
        :class="[
          'alert mb-0 py-2 px-3 small mt-2',
          document_review_feedback_tone === 'success'
            ? 'alert-success'
            : document_review_feedback_tone === 'danger'
              ? 'alert-danger'
              : 'alert-warning'
        ]"
      >
        {{ document_review_feedback }}
      </div>
    </div>
  </aside>

  <div
    v-if="preview_modal_visible"
    class="onboarding-case-detail-backdrop"
    @click="close_preview_modal"
  ></div>

  <section
    v-if="preview_modal_visible"
    class="queue-form-preview-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="preview_modal_title || 'Document preview'"
  >
    <header class="queue-form-preview-modal__header">
      <div>
        <div class="queue-form-preview-modal__eyebrow">Document preview</div>
        <h2 class="queue-form-preview-modal__title">{{ preview_modal_title }}</h2>
      </div>
      <div class="d-flex align-items-center">
        <button
          type="button"
          class="queue-form-preview-modal__close"
          aria-label="Close document preview"
          @click="close_preview_modal"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </header>

    <div class="queue-form-preview-modal__body">
      <img
        v-if="preview_modal_type === 'image'"
        :src="preview_modal_src()"
        alt="Selected document preview"
        class="queue-form-preview-modal__image"
      >
      <iframe
        v-else-if="preview_modal_type === 'pdf'"
        :src="preview_modal_src()"
        title="Document preview"
        class="queue-form-preview-modal__frame"
      ></iframe>
      <div v-else class="queue-form-preview-modal__fallback">
        <i class="bi bi-file-earmark-text"></i>
        <p class="mb-3">Preview is not available for this file type.</p>
        <a
          v-if="preview_modal_url"
          :href="preview_modal_url"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-primary"
        >
          Download or open file
        </a>
      </div>
    </div>
  </section>
</main>
